import { Flex, Modal, Select, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useGetBranchClientServicesQuery } from '@app/api/ClientServices';
import { EDIT, SERVICES } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import type { CopyServiceModalProps } from '@app/pages/Clients/EditClientService/components/ClientServiceProductUsages/components/CopyServiceModal/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import type { NClientServices } from '@app/types';

import type { DefaultOptionType } from 'antd/es/select';

const { Text } = Typography;

export const CopyServiceModal: React.FC<CopyServiceModalProps> = ({
  isOpen,
  setIsOpen,
  serviceId,
}) => {
  const [selectedValue, setSelectedValue] = useState<number | null>(null);

  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const { data: clientServicesData, error: clientServicesError } =
    useGetBranchClientServicesQuery({ ActiveBranchId });

  useEffect(() => {
    if (clientServicesError) {
      showNotification(handleCatchError(clientServicesError));
    }
  }, [clientServicesError, showNotification]);

  const handleCloseModal = () => {
    setIsOpen(false);
    setSelectedValue(null);
  };

  const adaptServicesForSelect = (
    data: NClientServices.ModelBranchClientService[] | undefined
  ): DefaultOptionType[] => {
    if (!data) return [];

    const filteredData = data.filter(({ Id }) => Id !== serviceId);

    const groups: Record<
      string,
      { label: string; options: DefaultOptionType[] }
    > = {};

    filteredData.forEach((item) => {
      const groupKey =
        item.CategoryId !== null && item.CategoryId !== undefined
          ? `cat-${item.CategoryId}`
          : `no-cat-${item.Id}`;

      if (!groups[groupKey]) {
        groups[groupKey] = {
          label: item.CategoryName || 'Без категории',
          options: [],
        };
      }

      groups[groupKey].options.push({
        key: `service-${item.Id}-group-${groupKey}`,
        label: item.Name,
        value: item.Id,
        filterName: item.Name?.toLowerCase(),
      });
    });

    return Object.values(groups);
  };

  const handleChange = (value: number) => {
    setSelectedValue(value);
  };

  const navigateToService = () => {
    navigate(
      `/${SERVICES}/${EDIT}/?id=${selectedValue}&key=3&copyingService=true`
    );
    handleCloseModal();
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleCloseModal}
      title={'Копировать технологическую карту'}
      okText={'Перейти в выбранную услугу'}
      onOk={navigateToService}
      okButtonProps={{
        disabled: !selectedValue,
      }}
    >
      <Flex vertical gap={16}>
        <Text>
          Выберите услугу, в которую хотите скопировать технологическую карту
        </Text>
        <Select
          options={adaptServicesForSelect(clientServicesData)}
          allowClear
          showSearch
          optionFilterProp="filterName"
          value={selectedValue}
          onChange={handleChange}
        />
      </Flex>
    </Modal>
  );
};
